import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from './api.js';
import { EmptyState, SkeletonTable, useToast, VoicemailIcon } from './ui.js';

/**
 * System audio.
 *
 * Currently one asset — hold music — but the section exists so the other
 * system-wide prompts have an obvious home as they arrive. Per-number
 * greetings stay under Numbers, where the number they belong to is.
 */
export function Audio(): JSX.Element {
  const toast = useToast();
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback((): void => {
    api.settings().then(setSettings).catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(load, [load]);

  const current = (settings?.['hold.music_name'] as string | null) ?? null;

  async function upload(file: File): Promise<void> {
    setBusy(true);
    try {
      await api.uploadHoldMusic(file);
      load();
      toast.push({ title: 'Hold music updated', tone: 'ok' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    try {
      await api.removeHoldMusic();
      load();
      toast.push({ title: 'Hold music removed', tone: 'ok' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>Audio</h2>
          <p className="section-sub">
            System-wide audio. Per-number greetings are set under Numbers.
          </p>
        </div>
      </div>

      {settings === null ? (
        <SkeletonTable rows={2} columns={[40, 30]} />
      ) : (
        <div className="card">
          <div className="greeting-slot">
            <div className="greeting-meta">
              <strong>Hold music</strong>
              <span>
                Played to whoever is put on hold, and to callers waiting during a
                transfer. Loops until the call is resumed.
              </span>
              {current ? (
                <span className="greeting-file">Current: {current}</span>
              ) : (
                <span className="greeting-file none">
                  None — callers hear silence on hold
                </span>
              )}
            </div>

            <div className="greeting-actions">
              <input
                ref={fileInput}
                type="file"
                accept="audio/wav,audio/mpeg,.wav,.mp3"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              >
                {busy ? 'Uploading…' : current ? 'Replace' : 'Upload'}
              </button>
              {current && (
                <button
                  type="button"
                  className="link danger"
                  disabled={busy}
                  onClick={() => void remove()}
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          <p className="hint" style={{ margin: '16px 0 0' }}>
            WAV or MP3, up to 5 MB. A short loop of 20–30 seconds works better than
            a full track — callers rarely wait long enough to hear more, and a
            smaller file is cheaper to stream on every held call. Changes take
            effect on the next call; no restart needed.
          </p>
        </div>
      )}
    </section>
  );
}

/** Kept so the icon import stays used if this section grows. */
export const AudioSectionIcon = VoicemailIcon;
export { EmptyState };
